'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown, BarChart3, CalendarDays } from 'lucide-react';
import { cn } from '@sharpr/ui';
import type { Sport } from '@sharpr/types';
import { useSportSeasons } from '@/hooks/useSportSeasons';
import { isWithinSeasonCtaWindow } from '@/services/billing/seasonClamp';
import { captureEvent, EVENTS } from '@/lib/posthog/events';

interface SportButtonsProps {
  disabled?: boolean;
  className?: string;
}

export function SportButtons({ disabled, className }: SportButtonsProps) {
  const pathname = usePathname();
  const activeSport: 'mlb' | 'nba' | null = pathname.startsWith('/mlb')
    ? 'mlb'
    : pathname.startsWith('/nba')
      ? 'nba'
      : null;
  const activeView: 'props' | 'slate' = pathname === '/mlb/slate' ? 'slate' : 'props';

  // Off-season sports get a disabled tab (greyed, non-navigable) — no games to show.
  // Fails open (in-season) until seasons load / on error, mirroring isWithinSeasonCtaWindow.
  // The ACTIVE sport's tab is never off-season-disabled — you're already viewing it.
  const seasons = useSportSeasons();
  const isOffSeason = (sport: Sport) =>
    !isWithinSeasonCtaWindow(seasons.find((s) => s.sport === sport));
  const mlbOff = isOffSeason('mlb');
  const nbaOff = isOffSeason('nba');
  // Entering the odds app from a marketing page (landing, pricing, …) is an acquisition
  // click; switching sports inside the app is not, so only the former reports.
  const trackEntry = (destination: string, label: string) => {
    if (activeSport === null) {
      captureEvent(EVENTS.CTA_CLICKED, { location: 'header_sport', label, destination });
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the MLB dropdown whenever the user navigates — no stale open menu on the new route.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {/* MLB Button with Dropdown */}
      <div className="relative">
        {activeSport === 'mlb' ? (
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            className={cn(
              'flex items-center gap-1 px-2 sm:px-2.5 py-1.5 sm:py-2 text-[11px] sm:text-xs font-semibold tracking-wide rounded-md',
              'transition-all [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] duration-300',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'text-gray-900 dark:text-text-primary border-b-2 border-[var(--brand-600)] dark:border-[var(--brand-500)]'
            )}
          >
            MLB
            <ChevronDown className={cn(
              'w-3 h-3 transition-transform duration-200',
              isOpen && 'rotate-180'
            )} />
          </button>
        ) : (
          <Link
            href="/mlb"
            prefetch
            onClick={() => trackEntry('/mlb', 'MLB')}
            aria-disabled={disabled || mlbOff}
            title={mlbOff ? 'MLB is off-season' : undefined}
            className={cn(
              'flex items-center gap-1 px-2 sm:px-2.5 py-1.5 sm:py-2 text-[11px] sm:text-xs font-semibold tracking-wide rounded-md',
              'transition-all [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] duration-300',
              (disabled || mlbOff) && 'opacity-50 pointer-events-none',
              activeSport === null
                ? 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100'
                : 'text-gray-400 dark:text-text-quaternary hover:text-gray-400 dark:hover:text-text-tertiary'
            )}
          >
            MLB
          </Link>
        )}

        {/* Dropdown Menu */}
        {isOpen && activeSport === 'mlb' && (
          <div
            ref={menuRef}
            role="menu"
            className="absolute top-full left-0 mt-2 min-w-[10rem] p-1.5 rounded-xl bg-white dark:bg-bg-secondary border border-border dark:border-border-secondary shadow-lg shadow-black/5 dark:shadow-black/40 z-50 origin-top-left animate-dropdown-in"
          >
            {([
              { view: 'props', label: 'Props', href: '/mlb', Icon: BarChart3 },
              { view: 'slate', label: 'Slate', href: '/mlb/slate', Icon: CalendarDays },
            ] as const).map(({ view, label, href, Icon }) => {
              const isActive = activeView === view;
              return (
                <Link
                  key={view}
                  role="menuitem"
                  href={href}
                  prefetch
                  className={cn(
                    'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-[var(--brand-500)]/10 text-[var(--brand-600)] dark:bg-[var(--brand-500)]/15 dark:text-[var(--brand-400)]'
                      : 'text-gray-500 dark:text-text-tertiary hover:bg-muted/60 dark:hover:bg-white/[0.04] hover:text-gray-900 dark:hover:text-text-primary'
                  )}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  {label}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* NBA Button */}
      {activeSport === 'nba' ? (
        <span
          aria-current="page"
          className={cn(
            'flex items-center gap-1 px-2 sm:px-2.5 py-1.5 sm:py-2 text-[11px] sm:text-xs font-semibold tracking-wide rounded-md',
            'transition-all [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] duration-300',
            'text-gray-900 dark:text-text-primary border-b-2 border-[var(--brand-600)] dark:border-[var(--brand-500)]'
          )}
        >
          NBA
        </span>
      ) : (
        <Link
          href="/nba"
          prefetch
          onClick={() => trackEntry('/nba', 'NBA')}
          aria-disabled={disabled || nbaOff}
          title={nbaOff ? 'NBA is off-season' : undefined}
          className={cn(
            'flex items-center gap-1 px-2 sm:px-2.5 py-1.5 sm:py-2 text-[11px] sm:text-xs font-semibold tracking-wide rounded-md',
            'transition-all [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] duration-300',
            (disabled || nbaOff) && 'opacity-50 pointer-events-none',
            activeSport === null
              ? 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100'
              : 'text-gray-400 dark:text-text-quaternary hover:text-gray-400 dark:hover:text-text-tertiary'
          )}
        >
          NBA
        </Link>
      )}
    </div>
  );
}
